import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { ChatUser } from '../../types';

interface Props {
  title: string;
  submitLabel: string;
  users: ChatUser[];
  maxSelectable?: number;
  onSubmit: (selectedIds: string[]) => Promise<void>;
  onCancel: () => void;
}

const LIST_HEIGHT = 360;

const UserSelectModal: React.FC<Props> = ({ title, submitLabel, users, maxSelectable, onSubmit, onCancel }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  // onSubmit unmounts this modal (via onCancel) once the invite resolves, so skip the trailing
  // setLoading in the finally below to avoid a state update on an unmounted component. Set the ref
  // in the effect body too (not just cleanup) so it survives React 18 StrictMode's mount→unmount→mount.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const selectedCount = Object.keys(selected).length;
  const isLimitReached = maxSelectable !== undefined && selectedCount >= maxSelectable;

  const filtered = users.filter((u) =>
    search.length < 2 ? true : u.username.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleToggle = useCallback(
    (userGUID: string) => {
      setSelected((prev) => {
        const next = { ...prev };
        if (next[userGUID]) {
          delete next[userGUID];
        } else if (maxSelectable === undefined || Object.keys(prev).length < maxSelectable) {
          next[userGUID] = true;
        }
        return next;
      });
    },
    [maxSelectable],
  );

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      await onSubmit(Object.keys(selected));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [selected, onSubmit]);

  return (
    <Dialog open fullWidth maxWidth="xs" fullScreen={isMobile} onClose={onCancel} sx={{ zIndex: 1700 }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
        {title}
        <IconButton onClick={onCancel} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={formatMessage({ id: 'chat.channelList.searchPlaceholder', defaultMessage: 'Search' })}
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Typography
            variant="body2"
            color={selectedCount > 0 ? 'primary' : 'text.secondary'}
            sx={{ mt: 1 }}
          >
            {formatMessage(
              { id: 'chat.userSelectModal.selected', defaultMessage: '{count} selected' },
              { count: selectedCount },
            )}
          </Typography>
          {isLimitReached && (
            <Typography variant="body2" color="warning.main">
              {formatMessage({
                id: 'chat.userSelectModal.limitReached',
                defaultMessage: 'Group limit of 12 participants reached',
              })}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            height: isMobile ? `calc(100vh - 220px)` : LIST_HEIGHT,
            overflowY: 'auto',
            flexShrink: 0,
          }}
        >
          {filtered.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" height="100%">
              <Typography variant="body2" color="text.secondary">
                {formatMessage({ id: 'chat.userSelectModal.noUsersFound', defaultMessage: 'No users found' })}
              </Typography>
            </Stack>
          ) : (
            filtered.map((u) => {
              const isRowDisabled = isLimitReached && !selected[u.userGUID];
              return (
                <ListItem
                  key={u.userGUID}
                  divider
                  onClick={() => handleToggle(u.userGUID)}
                  sx={{ cursor: isRowDisabled ? 'default' : 'pointer', px: 2, opacity: isRowDisabled ? 0.45 : 1 }}
                >
                  <ListItemAvatar>
                    <Avatar src={u.picture} alt={u.username} />
                  </ListItemAvatar>
                  <ListItemText primary={u.username} />
                  <Checkbox edge="end" checked={!!selected[u.userGUID]} tabIndex={-1} disableRipple disabled={isRowDisabled} />
                </ListItem>
              );
            })
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={onCancel} disabled={loading}>
          {formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={selectedCount === 0 || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserSelectModal;
